import type { ReactNode } from 'react';
import { Box, Center, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { RecentActivityItem } from '@/api/generated/models';
import { CardSection, ListPagination } from '@/components/common';
import { PanelInfoIcon } from '@/features/board/components/PanelInfoIcon';
import {
  formatRecentActivity,
  formatRelativeTimestamp,
  recentActivityHref
} from '@/features/dashboard/pmDashboardDisplay';
import { useLocalPagination } from '@/utils/useLocalPagination';

/** Isti prag kao PmTaskTable — ispod ovog broja stavki paging kontrole se ne prikazuju. */
const PAGING_THRESHOLD = 11;

/** Jednostavna vertikalna lista (jedan item ispod drugog) — NE 3-stupčani grid kao u Figmi, po
 * izričitom korisnikovom zahtjevu. */
export const RecentActivityList = ({
  title,
  info,
  items
}: {
  title: string;
  info?: ReactNode;
  items: RecentActivityItem[];
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { page, setPage, pageSize, setPageSize, totalPages, pageItems } = useLocalPagination(items);

  return (
    <CardSection
      title={title}
      headerRight={info ? <PanelInfoIcon label={info} width={280} title={title} /> : undefined}
    >
      {items.length === 0 ? (
        <Center py={32}>
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t('pmDashboard.activity.empty')}
          </Text>
        </Center>
      ) : (
        <Stack gap={0} p={16}>
          {pageItems.map((item, index) => {
            const href = recentActivityHref(item);
            return (
              <Group
                key={item.id}
                gap={10}
                wrap="nowrap"
                py={10}
                role={href ? 'button' : undefined}
                tabIndex={href ? 0 : undefined}
                onClick={() => href && navigate(href)}
                onKeyDown={(event) => {
                  if ((event.key === 'Enter' || event.key === ' ') && href) navigate(href);
                }}
                style={{
                  borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)',
                  cursor: href ? 'pointer' : undefined
                }}
              >
                <Box
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    flexShrink: 0,
                    backgroundColor: 'var(--color-accent-primary)'
                  }}
                />
                <Text fz={13.5} style={{ color: 'var(--color-text-primary)', flex: 1 }}>
                  {formatRecentActivity(t, item)}
                </Text>
                <Text fz={12} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}>
                  {formatRelativeTimestamp(t, item.timestamp)}
                </Text>
              </Group>
            );
          })}
        </Stack>
      )}
      {items.length > 0 &&
        (() => {
          const label = t('pmDashboard.activity.showing', {
            shown: pageItems.length,
            total: items.length
          });
          return items.length < PAGING_THRESHOLD ? (
            <Text
              fz={11.5}
              style={{
                color: 'var(--color-text-muted)',
                padding: '14px 20px',
                borderTop: '1px solid var(--color-border-soft)'
              }}
            >
              {label}
            </Text>
          ) : (
            <ListPagination
              label={label}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
          );
        })()}
    </CardSection>
  );
};
